import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";

/**
 * FeatureGate - Conditionally renders children based on feature flag
 * 
 * Usage:
 * <FeatureGate enabled={process.env.REACT_APP_ENABLE_FEATURE === "1"} redirectTo="/dashboard">
 *   <FeatureComponent />
 * </FeatureGate>
 * 
 * @param {boolean} enabled - Whether the feature is enabled
 * @param {string} redirectTo - Where to redirect if feature is disabled (default: "/")
 * @param {string} featureName - Name of the feature for the unavailable message
 * @param {boolean} showGatePage - If true, shows a gate page instead of redirecting
 * @param {React.ReactNode} children - Content to render if enabled
 */
export default function FeatureGate({ 
  enabled, 
  redirectTo = "/", 
  featureName = "This feature",
  showGatePage = true,
  children 
}) {
  const navigate = useNavigate();
  
  if (!enabled) {
    // Show a proper gate page instead of silent redirect
    if (showGatePage) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950/30 to-slate-950 flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            {/* Back button */}
            <button
              onClick={() => navigate(-1)}
              className="absolute top-4 left-4 flex items-center gap-2 text-slate-400 hover:text-slate-300 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm">Back</span>
            </button>
            
            {/* Gate content */}
            <div className="surface-card rounded-2xl p-8 bg-slate-900/80 border border-slate-700">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-slate-800 flex items-center justify-center">
                <Lock className="w-8 h-8 text-slate-500" />
              </div>
              
              <h1 className="text-xl font-semibold text-slate-100 mb-2">
                Coming Soon
              </h1>
              
              <p className="text-slate-400 text-sm mb-6">
                {featureName} is currently unavailable. Check back soon!
              </p>
              
              <button
                onClick={() => navigate(redirectTo)}
                className="px-6 py-2.5 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    // Legacy behavior: redirect
    return <Navigate to={redirectTo} replace />;
  }
  
  return <>{children}</>;
}
